import EntityMember from "_shared/entity-members/entity-member";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import { FeeScheduleCategoryMaskedCurrency } from "work/masked-fields";

export type FeeScheduleTemplateCategory = FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency;

/**
 * Strips the person a category names.
 *
 * A fee schedule template carries its staffing now (#5237), so this is no longer what every
 * crossing into a proposal does. Two things still need it:
 *
 * - the category catalog, whose rows are shared by every template in the firm and therefore carry
 *   no person of their own;
 * - the "leave the rate unassigned" answer, when a template names somebody who is not on the
 *   proposal's vendor team. See `withStaffingLimitedToTeam`.
 */
export function withoutFeeScheduleTeamMember<T extends FeeScheduleTemplateCategory>(category: T): T {
  if (category instanceof FeeScheduleCategoryMaskedCurrency) {
    return new FeeScheduleCategoryMaskedCurrency({
      id: category.id.clone(),
      name: category.name.clone(),
      description: category.description?.clone() ?? category.description,
      billingCode: category.billingCode?.clone() ?? category.billingCode,
      teamMember: null,
      teamMemberIsMasked: false,
      version: category.version,
    }) as T;
  }

  return new FeeScheduleCategory({
    id: category.id.clone(),
    name: category.name.clone(),
    description: category.description?.clone() ?? category.description,
    fee: category.fee?.clone() ?? category.fee,
    billingCode: category.billingCode?.clone() ?? category.billingCode,
    teamMember: null,
    teamMemberIsMasked: false,
    version: category.version,
  }) as T;
}

/**
 * The same category, for somebody else.
 *
 * The sibling of `withoutFeeScheduleTeamMember`, and it exists for one answer: applying a template
 * whose rate names a person the proposal's team does not have, where the user chooses somebody it
 * does have rather than adding a stranger. See `withStaffingDecisionsApplied`.
 *
 * Everything but the person is carried across identically. That is not tidiness — the draft fee
 * schedule tab dedups these by `name` and the active one keys them by `id`, so a substitution that
 * minted either would read as a different category rather than the same rate for someone else.
 */
export function withFeeScheduleTeamMember<T extends FeeScheduleTemplateCategory>(
  category: T,
  teamMember: EntityMember,
): T {
  if (category instanceof FeeScheduleCategoryMaskedCurrency) {
    return new FeeScheduleCategoryMaskedCurrency({
      id: category.id.clone(),
      name: category.name.clone(),
      description: category.description?.clone() ?? category.description,
      billingCode: category.billingCode?.clone() ?? category.billingCode,
      teamMember,
      teamMemberIsMasked: false,
      version: category.version,
    }) as T;
  }

  return new FeeScheduleCategory({
    id: category.id.clone(),
    name: category.name.clone(),
    description: category.description?.clone() ?? category.description,
    fee: category.fee?.clone() ?? category.fee,
    billingCode: category.billingCode?.clone() ?? category.billingCode,
    teamMember,
    teamMemberIsMasked: false,
    version: category.version,
  }) as T;
}
